import React from 'react';
import { FileText, Download, Trash2 } from 'lucide-react';
import { docTypeBadge, fmtBytes } from './constants';

// The request's current attachments INSIDE a Modify modal, with per-file
// Remove/Undo (docs/workflow-ui.md §4.3). The read-only
// counterpart on the detail page itself is AttachmentsList.
//
// Why this exists: without it a Modify modal could only ADD files. A
// requester whose declaration was rejected because the attachment was wrong
// would have no way to take the wrong one off — the reviewer would end up
// looking at both.
//
// Marking is deferred, not immediate: files marked here are deleted when the
// modal is SAVED, so a mis-click is undone with Undo and Cancel throws the
// whole thing away. The parent owns `removeIds` (it has to send them with the
// rest of the form) and passes them straight back down.
//
//   files        — [{ id, originalName, sizeBytes, docType }]
//   removeIds    — ids currently marked for removal
//   onToggle     — (id) => void
//   downloadHref — (f) => url  (the link is hidden once marked)
//   badgeFor     — optional (f) => [label, cls] override
//   label        — section label (default "Current Attachments")
//   hint         — optional line under the label
const EditableAttachments = ({
  files = [],
  removeIds = [],
  onToggle,
  downloadHref,
  badgeFor = null,
  label = 'Current Attachments',
  hint = null,
  ...rest
}) => {
  if (process.env.NODE_ENV !== 'production') {
    // A mis-wired caller renders perfectly and only throws on the first
    // Remove click — name the mistake loudly instead.
    if ('onToggleRemove' in rest) {
      console.error('EditableAttachments: unknown prop "onToggleRemove" — the Remove/Undo callback is called "onToggle".');
    }
    if ('onPick' in rest || 'accept' in rest) {
      console.error('EditableAttachments: unknown prop "onPick"/"accept" — this component only lists EXISTING files. Render a FormKit FilePicker next to it for new uploads.');
    }
    if (typeof onToggle !== 'function') {
      console.error('EditableAttachments: "onToggle" is required — without it every Remove/Undo button throws on click.');
    }
  }
  if (files.length === 0) return null;
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      {hint && <p className="text-xs text-slate-400 mb-1.5">{hint}</p>}
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {files.map((f) => {
          const marked = removeIds.includes(f.id);
          const [badgeLabel, badgeCls] = badgeFor ? badgeFor(f) : docTypeBadge(f.docType);
          return (
            <li key={f.id} className="flex items-center justify-between px-3 py-2 gap-2">
              <span className={`flex items-center gap-2 text-sm min-w-0 ${marked ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                <FileText className="w-4 h-4 shrink-0 text-slate-400" />
                <span className="truncate">{f.originalName}</span>
                {badgeLabel && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 no-underline ${badgeCls}`}>
                    {badgeLabel}
                  </span>
                )}
                {f.sizeBytes != null && <span className="text-xs text-slate-500 shrink-0">{fmtBytes(f.sizeBytes)}</span>}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {!marked && downloadHref && (
                  <a
                    href={downloadHref(f)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Download className="w-4 h-4" /> Download
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => onToggle(f.id)}
                  className={`inline-flex items-center gap-1 text-sm ${marked ? 'text-slate-500 hover:text-slate-700' : 'text-red-600 hover:text-red-800'}`}
                >
                  {marked ? 'Undo' : (<><Trash2 className="w-4 h-4" /> Remove</>)}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {removeIds.length > 0 && (
        <p className="text-xs text-slate-500 mt-1">
          Marked attachments are deleted when you save — this cannot be undone afterwards.
        </p>
      )}
    </div>
  );
};

/** The state every Modify modal needs for the list above. */
export const useRemovableFiles = () => {
  const [removeIds, setRemoveIds] = React.useState([]);
  const toggle = (id) => setRemoveIds((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));
  /** Append to a FormData only when something is marked. */
  const appendTo = (form) => {
    if (removeIds.length > 0) form.set('removeFileIds', JSON.stringify(removeIds));
  };
  /** Files that survive this save — for client-side "is anything left" checks. */
  const kept = (files = []) => files.filter((f) => !removeIds.includes(f.id));
  return { removeIds, toggle, appendTo, kept };
};

export default EditableAttachments;
